/**
 * Contextual severity escalation of catalog defaults (docs/event-model.md
 * §5): fetched or decoded code, probing, long delays and date gates, hidden
 * iframes, third-party scripts, sensitive listeners, plus the notable
 * capability findings (third-party requests, storage writes, cookie reads,
 * fingerprinting, new globals). Owned by Phase 4 lane C.
 *
 * Every rule here is pure and reads only the log, the layers, the context,
 * the run's code, and the global diff. Canary flows, tampering, and evasion
 * have their own rule files.
 */
import { SENSITIVE_FIELD_TOKENS } from "../../environments/browser/dom";
import {
  WATCHED_GLOBALS,
  type WatcherFamily,
  watcherFamily,
} from "../../environments/browser/watchers";
import type { AuditEvent, SerializedValue, Severity } from "../../events";
import { matchRoute } from "../../network";
import { formatVirtualTime } from "../../timeline";
import { originOf, resolveUrl } from "../../url";
import { isThirdParty } from "../../url/party";
import { findDateGates } from "../dategate";
import type { Rule, RuleEscalation, RuleFinding, RuleOutput } from "../types";

// ── Helpers ─────────────────────────────────────────────────────────────────

const raise = (seqs: Iterable<number>, severity: Severity): RuleEscalation[] =>
  [...seqs].map((seq) => ({ seq, severity }));

/** Groups `items` by key, keeping first-seen order. */
function groupBy<T>(items: Iterable<T>, key: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    const group = groups.get(k);
    if (group) group.push(item);
    else groups.set(k, [item]);
  }
  return groups;
}

const WINDOW_PREFIX = "window.";
const withoutWindow = (target: string) =>
  target.startsWith(WINDOW_PREFIX) ? target.slice(WINDOW_PREFIX.length) : target;

const unique = <T>(items: Iterable<T>): T[] => [...new Set(items)];
const seqs = (events: readonly AuditEvent[]) => events.map((e) => e.seq);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A string value's text, or the kept prefix of a truncated one. */
export function textOf(
  v: SerializedValue | undefined,
): { text: string; truncated: boolean } | null {
  if (typeof v === "string") return { text: v, truncated: false };
  if (v !== null && typeof v === "object" && "$str" in v) return { text: v.$str, truncated: true };
  return null;
}

/** Whether `part` (at least `min` chars, trimmed) is in `whole`, raw or JSON-escaped. */
function contains(whole: string, part: string, min: number): boolean {
  const trimmed = part.trim();
  if (trimmed.length < min) return false;
  return whole.includes(trimmed) || whole.includes(JSON.stringify(trimmed).slice(1, -1));
}

// ── Code from elsewhere ─────────────────────────────────────────────────────

/** Shortest response body, or payload, that counts as the same code. */
const MIN_CODE = 16;

const fetchedCode: Rule = {
  id: "escalation.fetched-code",
  explanation:
    "Code that arrived over the network was executed. What runs is decided by a server at run time, not by the reviewed source, so it can change at any moment (a classic two-stage loader).",
  apply({ events, context }) {
    const findings: RuleFinding[] = [];
    const escalations: RuleEscalation[] = [];
    const responses: { event: AuditEvent; body: string }[] = [];
    for (const event of events) {
      if (event.network) {
        const route = matchRoute(context.mockRoutes, event.network.method, event.network.url);
        if (route?.body?.trim()) responses.push({ event, body: route.body });
        continue;
      }
      const payload = event.codegen?.payload;
      if (event.category !== "codegen" || payload === undefined) continue;
      const source = responses.find(
        ({ body }) =>
          (body.trim() !== "" && body.trim() === payload.trim()) ||
          contains(payload, body, MIN_CODE) ||
          contains(body, payload, MIN_CODE),
      );
      if (!source) continue;
      const url = source.event.network?.url ?? "";
      findings.push({
        severity: "critical",
        title: `Ran code fetched from ${url}`,
        evidence: [source.event.seq, event.seq],
        target: url,
        tags: ["remote-code"],
      });
      escalations.push(...raise([source.event.seq, event.seq], "critical"));
    }
    return { findings, escalations };
  },
};

/** Shortest decoded text that counts as decoding code. */
const MIN_DECODED = 8;

const decodedCode: Rule = {
  id: "escalation.decoded-code",
  explanation:
    "A string was decoded (atob, String.fromCharCode, unescape) and the result was run as code. Encoding a payload hides what it does from anyone reading the source.",
  apply({ events }) {
    const findings: RuleFinding[] = [];
    const escalations: RuleEscalation[] = [];
    const decodes: { event: AuditEvent; text: string; truncated: boolean }[] = [];
    for (const event of events) {
      if (event.op === "decode") {
        const result = textOf(event.result);
        if (result && result.text.trim().length >= MIN_DECODED) decodes.push({ event, ...result });
        continue;
      }
      const payload = event.codegen?.payload;
      if (event.category !== "codegen" || payload === undefined) continue;
      const source = decodes.find(
        ({ text, truncated }) =>
          contains(payload, text, MIN_DECODED) ||
          (!truncated && contains(text, payload, MIN_DECODED)),
      );
      if (!source) continue;
      const decoder = source.event.target;
      findings.push({
        severity: "suspicious",
        title: `Ran code decoded with ${withoutWindow(decoder)}`,
        evidence: [source.event.seq, event.seq],
        target: decoder,
        tags: ["decoded"],
      });
      escalations.push(...raise([source.event.seq, event.seq], "suspicious"));
    }
    return { findings, escalations };
  },
};

// ── Looking around ──────────────────────────────────────────────────────────

/** How many distinct watcher globals make probing suspicious. */
const PROBE_THRESHOLD = 3;

/** The top-level global a `window.x` probe looked up. */
const probedName = (e: AuditEvent): string | null =>
  e.category === "probe" && e.target.startsWith("window.")
    ? (e.target.slice("window.".length).split(".")[0] ?? null)
    : null;

const probing: Rule = {
  id: "escalation.probing",
  explanation:
    "The script looked for analytics, security, or wallet globals. Checking who is watching (to stay quiet when observed) or what is worth stealing is typical of malicious code; three or more is suspicious.",
  apply({ events }) {
    const hits: { event: AuditEvent; name: string; family: WatcherFamily }[] = [];
    for (const event of events) {
      const name = probedName(event);
      const family = name === null ? null : watcherFamily(name);
      if (name !== null && family) hits.push({ event, name, family });
    }
    if (hits.length === 0) return {};
    const names = unique(hits.map((h) => h.name));
    const severity: Severity = names.length >= PROBE_THRESHOLD ? "suspicious" : "notable";
    const families = (Object.keys(WATCHED_GLOBALS) as WatcherFamily[])
      .map((family) => ({
        family,
        count: unique(hits.filter((h) => h.family === family).map((h) => h.name)).length,
      }))
      .filter((f) => f.count > 0);
    const [single] = families;
    const title =
      families.length === 1 && single
        ? `Probed ${plural(single.count, `${single.family} global`)}`
        : `Probed ${names.length} watcher globals (${families.map((f) => `${f.count} ${f.family}`).join(", ")})`;
    const evidence = hits.map((h) => h.event.seq);
    return {
      findings: [{ severity, title, evidence, tags: families.map((f) => f.family) }],
      escalations: raise(evidence, severity),
    };
  },
};

// ── Time ────────────────────────────────────────────────────────────────────

const HOUR = 3_600_000;

const longDelay: Rule = {
  id: "escalation.long-delay",
  explanation:
    "Code was scheduled more than an hour out. Waiting outlasts sandboxes and reviews that only watch for seconds; the virtual clock fast-forwarded to it.",
  apply({ events }) {
    const findings: RuleFinding[] = [];
    const escalations: RuleEscalation[] = [];
    for (const call of events) {
      if (call.category !== "timers" || call.op !== "call" || !call.timer) continue;
      if (call.timer.delay <= HOUR) continue;
      const { id } = call.timer;
      const fired = events.filter((e) => e.seq > call.seq && e.op === "fire" && e.timer?.id === id);
      const evidence = [call.seq, ...seqs(fired)];
      findings.push({
        severity: "suspicious",
        title: `Scheduled code ${formatVirtualTime(call.timer.delay)} out (${call.target})`,
        evidence,
        target: call.target,
        tags: ["delayed"],
      });
      escalations.push(...raise(evidence, "suspicious"));
    }
    return { findings, escalations };
  },
};

/** `YYYY-MM-DD` of an epoch-ms date (UTC). */
export const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const dateGate: Rule = {
  id: "escalation.date-gate",
  explanation:
    "The code compares the current date with a fixed date and behaves differently on either side of it. A single run only sees one side: a time bomb looks harmless until its date.",
  apply({ events, layers, code }) {
    const sources = [
      ...(code === undefined ? [] : [{ layer: 0, code }]),
      ...layers.map((l) => ({ layer: l.id, code: l.code })),
    ];
    const findings: RuleFinding[] = [];
    const escalations: RuleEscalation[] = [];
    for (const source of sources) {
      for (const gate of findDateGates(source.code)) {
        const inRegion = events.filter(
          (e) =>
            e.site?.layer === source.layer &&
            e.site.line >= gate.region.from &&
            e.site.line <= gate.region.to,
        );
        const where =
          source.layer === 0 ? `line ${gate.line}` : `layer ${source.layer}, line ${gate.line}`;
        const date = gate.date === undefined ? null : isoDay(gate.date);
        findings.push({
          severity: "suspicious",
          title: `Date-gated branch: compares the date with ${date ?? "a fixed date"} (${where})`,
          evidence: seqs(inRegion),
          ...(date !== null && { target: date }),
          tags: ["date-gate"],
        });
        escalations.push(...raise(seqs(inRegion), "suspicious"));
      }
    }
    return { findings, escalations };
  },
};

// ── The page ────────────────────────────────────────────────────────────────

const HTML_SINK_TARGET = /\.(innerHTML|outerHTML|insertAdjacentHTML|write|writeln)$/;
const IFRAME_TAG = /<iframe\b[^>]*>/gi;
const HIDDEN = [
  /\shidden(\s|=|>|$)/i,
  /display\s*:\s*none/i,
  /visibility\s*:\s*hidden/i,
  /\s(width|height)\s*=\s*["']?[01](px)?(["'\s>]|$)/i,
  /(^|[;"'\s])(width|height)\s*:\s*[01](px)?\s*(;|["']|$)/i,
  /opacity\s*:\s*0(\.0+)?\s*(;|["']|$)/i,
];
const SRC = /\ssrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

const hiddenIframe: Rule = {
  id: "escalation.hidden-iframe",
  explanation:
    "An invisible iframe was injected. Hidden frames load third-party pages the user never sees: ad fraud, cookie syncing, clickjacking, or a covert channel.",
  apply({ events, context }) {
    const findings: RuleFinding[] = [];
    const escalations: RuleEscalation[] = [];
    for (const event of events) {
      if (event.category !== "dom" || !HTML_SINK_TARGET.test(event.target)) continue;
      const html = event.args.map((a) => textOf(a)?.text ?? "").join("");
      for (const [tag] of html.matchAll(IFRAME_TAG)) {
        if (!HIDDEN.some((re) => re.test(tag))) continue;
        const m = SRC.exec(tag);
        const src = m ? (m[1] ?? m[2] ?? m[3] ?? "") : "";
        const url = src === "" ? null : resolveUrl(src, context.url);
        const load = events.find(
          (e) =>
            url !== null &&
            e.seq > event.seq &&
            e.target === "HTMLIFrameElement.src" &&
            e.network?.url === url,
        );
        const origin = url === null ? "null" : originOf(url);
        const evidence = [event.seq, ...(load ? [load.seq] : [])];
        findings.push({
          severity: "suspicious",
          title: `Injected a hidden iframe${origin !== "null" ? ` (${origin})` : ""}`,
          evidence,
          ...(origin !== "null" && { target: origin }),
          tags: ["hidden-iframe"],
        });
        escalations.push(...raise(evidence, "suspicious"));
      }
    }
    return { findings, escalations };
  },
};

const INPUT_TAG = /<(input|textarea|select)\b[^>]*>/gi;
const FIELD_ATTR =
  /\s(?:name|id|autocomplete|placeholder|type)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi;

/** The sensitive tokens an injected field's attributes mention. */
function sensitiveTokens(tag: string): string[] {
  const tokens: string[] = [];
  for (const m of tag.matchAll(FIELD_ATTR)) {
    const value = (m[1] ?? m[2] ?? m[3] ?? "").toLowerCase();
    for (const token of SENSITIVE_FIELD_TOKENS) {
      if (value.includes(token) && !tokens.includes(token)) tokens.push(token);
    }
  }
  // One word per field: `name="card" placeholder="Card number"` is one card field.
  return tokens.slice(0, 1);
}

const injectedForm: Rule = {
  id: "escalation.injected-form",
  explanation:
    'The script added its own payment or credential inputs to the page. A fake "verify your card" or login overlay is how skimmers phish data the page never asked for.',
  apply({ events }) {
    const findings: RuleFinding[] = [];
    const escalations: RuleEscalation[] = [];
    for (const event of events) {
      if (event.category !== "dom" || !HTML_SINK_TARGET.test(event.target)) continue;
      const html = event.args.map((a) => textOf(a)?.text ?? "").join("");
      const fields = unique([...html.matchAll(INPUT_TAG)].flatMap(([tag]) => sensitiveTokens(tag)));
      if (fields.length === 0) continue;
      findings.push({
        severity: "suspicious",
        title: `Injected inputs for ${fields.join(", ")}`,
        evidence: [event.seq],
        ...(event.node !== undefined && { target: event.node }),
        tags: ["injected-form"],
      });
      escalations.push(...raise([event.seq], "suspicious"));
    }
    return { findings, escalations };
  },
};

/** The URL of injected or imported script code, if the event is one. */
function scriptUrl(e: AuditEvent): string | null {
  if (e.network && e.target === "HTMLScriptElement.src") return e.network.url;
  const url = e.codegen?.module?.url;
  return e.op === "codeload" && url && /^https?:/.test(url) ? url : null;
}

const thirdPartyScript: Rule = {
  id: "escalation.third-party-script",
  explanation:
    "Script from another site was added to the page. It runs with the page's full privileges, and whoever controls that server decides what it does.",
  apply({ events, context }) {
    const scripts = events.filter((e) => {
      const url = scriptUrl(e);
      return url !== null && isThirdParty(url, context.url) === true;
    });
    const byOrigin = groupBy(scripts, (e) => originOf(scriptUrl(e) ?? ""));
    const findings: RuleFinding[] = [...byOrigin].map(([origin, group]) => ({
      severity: "suspicious",
      title: `Injected a third-party script from ${origin}`,
      evidence: seqs(group),
      target: origin,
      tags: ["third-party-script"],
    }));
    return { findings, escalations: raise(seqs(scripts), "suspicious") };
  },
};

const KEY_EVENTS = ["keydown", "keyup", "keypress", "input", "beforeinput", "change", "paste"];
const SUBMIT_EVENTS = ["submit", "formdata"];
const PAGE_NODES = ["window", "#document", "body"];

/** Whether a node description (`input#cc-number[name=cc-number]`) names a sensitive field. */
export function isSensitiveNode(node: string): boolean {
  if (!/^(input|textarea|select)\b/.test(node)) return false;
  const lower = node.toLowerCase();
  return SENSITIVE_FIELD_TOKENS.some((token) => lower.includes(token));
}

/** The listened-for event type, for `addEventListener` calls and `on<type>` sets. */
export function listenedType(e: AuditEvent): string | null {
  if (e.category !== "dom") return null;
  if (e.op === "call" && e.target.endsWith(".addEventListener")) {
    const [type] = e.args;
    return typeof type === "string" ? type.toLowerCase() : null;
  }
  const handler = /\.on([a-z]+)$/.exec(e.target);
  return e.op === "set" && handler?.[1] ? handler[1] : null;
}

function isSensitiveListener(e: AuditEvent): boolean {
  const type = listenedType(e);
  const node = e.node ?? "";
  if (type === null) return false;
  if (KEY_EVENTS.includes(type)) return isSensitiveNode(node) || PAGE_NODES.includes(node);
  return SUBMIT_EVENTS.includes(type) && (node.startsWith("form") || PAGE_NODES.includes(node));
}

const sensitiveListener: Rule = {
  id: "escalation.sensitive-listener",
  explanation:
    "The script listens for keystrokes or form submissions on payment or credential fields, or on the whole page. That is how skimmers and keyloggers capture what the user types.",
  apply({ events }) {
    const listeners = events.filter(isSensitiveListener);
    const byNode = groupBy(listeners, (e) => e.node ?? "?");
    const findings: RuleFinding[] = [...byNode].map(([node, group]) => ({
      severity: "suspicious",
      title: `Listens for ${unique(group.map((e) => listenedType(e) ?? "")).join(", ")} on ${node}`,
      evidence: seqs(group),
      target: node,
      tags: ["listener"],
    }));
    return { findings, escalations: raise(seqs(listeners), "suspicious") };
  },
};

// ── Notable capabilities ────────────────────────────────────────────────────

const thirdPartyRequest: Rule = {
  id: "escalation.third-party-request",
  explanation:
    "The script contacted another site. Any third-party request can carry data out of the page, and needs a connect-src (or img-src, frame-src, …) grant.",
  apply({ events, context }) {
    const requests = events.filter(
      (e) => e.network !== undefined && isThirdParty(e.network.url, context.url) === true,
    );
    const byOrigin = groupBy(requests, (e) => originOf(e.network?.url ?? ""));
    const findings: RuleFinding[] = [...byOrigin].map(([origin, group]) => ({
      severity: "notable",
      title: `Contacted ${origin} (${plural(group.length, "request")})`,
      evidence: seqs(group),
      target: origin,
      tags: ["third-party"],
    }));
    return { findings, escalations: raise(seqs(requests), "notable") };
  },
};

/** One notable finding over the events `select` picks, or nothing. */
function notable(
  events: readonly AuditEvent[],
  select: (e: AuditEvent) => boolean,
  title: (picked: AuditEvent[]) => string,
  tags: string[],
): RuleOutput {
  const picked = events.filter(select);
  if (picked.length === 0) return {};
  return {
    findings: [{ severity: "notable", title: title(picked), evidence: seqs(picked), tags }],
    escalations: raise(seqs(picked), "notable"),
  };
}

const storageWrite: Rule = {
  id: "escalation.storage-write",
  explanation:
    "The script stored data in the browser (cookies, Web Storage, IndexedDB, Cache API). Stored data persists across visits and can track the user or stage a later payload.",
  apply: ({ events }) =>
    notable(
      events,
      (e) => e.category === "storage" && e.tags.includes("storage-write"),
      (picked) => `Wrote to storage: ${unique(picked.map((e) => e.target)).join(", ")}`,
      ["storage-write"],
    ),
};

const cookieRead: Rule = {
  id: "escalation.cookie-read",
  explanation:
    "The script read document.cookie, which holds session identifiers and tracking ids for this site.",
  apply: ({ events }) =>
    notable(
      events,
      (e) => e.category === "storage" && e.op === "get" && e.target === "document.cookie",
      () => "Read document.cookie",
      ["cookie-read"],
    ),
};

const fingerprinting: Rule = {
  id: "escalation.fingerprinting",
  explanation:
    "The script read device and browser traits (user agent, screen, languages, time zone, canvas, …). Combined, they identify a browser without cookies.",
  apply: ({ events }) =>
    notable(
      events,
      (e) => e.category === "fingerprint",
      (picked) =>
        `Read ${plural(unique(picked.map((e) => e.target)).length, "fingerprinting surface")}`,
      ["fingerprint"],
    ),
};

const newGlobals: Rule = {
  id: "escalation.new-globals",
  explanation:
    "The script left new globals on window. Globals are how injected code hands state or hooks to later stages, and they can collide with the page's own code.",
  apply({ events, globalDiff }) {
    const sets = events.filter(
      (e) =>
        e.category === "globals" &&
        e.op === "set" &&
        !e.tags.includes("decoy") &&
        e.target.startsWith("window."),
    );
    const names = unique([
      ...sets.map((e) => e.target.slice("window.".length)),
      ...globalDiff.filter((c) => c.change === "added").map((c) => c.path),
    ]);
    if (names.length === 0) return {};
    const shown = names.slice(0, 5).join(", ") + (names.length > 5 ? ", …" : "");
    return {
      findings: [
        {
          severity: "notable",
          title: `Created ${plural(names.length, "new global")}: ${shown}`,
          evidence: seqs(sets),
          tags: ["new-global"],
        },
      ],
      escalations: raise(seqs(sets), "notable"),
    };
  },
};

export const ESCALATION_RULES: readonly Rule[] = [
  fetchedCode,
  decodedCode,
  probing,
  longDelay,
  dateGate,
  hiddenIframe,
  injectedForm,
  thirdPartyScript,
  sensitiveListener,
  thirdPartyRequest,
  storageWrite,
  cookieRead,
  fingerprinting,
  newGlobals,
];
